// firebase.ts
import { initializeApp, getApps } from 'firebase/app';
import type { AppCheck } from 'firebase/app-check';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

export const isDev = process.env.NODE_ENV === 'development';
/** Build de bundle (CRA `react-scripts build`), usado tanto por produção quanto por staging. */
const isBundleBuild = process.env.NODE_ENV === 'production';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY!,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN!,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID!,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET!,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID!,
  appId: process.env.REACT_APP_FIREBASE_APP_ID!,
};

const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

// Serviços
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');
/** Callables Gen 2 em southamerica-east1 (ex.: delivery queue, analytics). */
export const functionsSa = getFunctions(app, 'southamerica-east1');
export const storage = getStorage(app);

// ── Firebase App Check (REEDY-23) ────────────────────────────────────────────
// Barreira de proveniência exigida pelas Cloud Functions administrativas
// (`.agents/memory/contracts.md` §"App Check — requisito transversal de autenticação
// administrativa"). É ADITIVA: não substitui `verifyAdminAuthV1`/claim `admin`.
// Um único `initializeAppCheck` no único `FirebaseApp` cobre as duas instâncias de
// Functions (`functions` us-central1 e `functionsSa` southamerica-east1); o SDK anexa
// `X-Firebase-AppCheck` sozinho em toda chamada `httpsCallable`.

const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_SITE_KEY;
const appCheckDebugToken = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;

/**
 * Site key inexistente usada só em dev. Com `FIREBASE_APPCHECK_DEBUG_TOKEN` setado, o SDK
 * troca o debug token direto no endpoint de exchange e nunca chama
 * `ReCaptchaV3Provider.getToken()` (@firebase/app-check: branch `isDebugMode()` em `getToken`);
 * a carga do script do reCAPTCHA feita por `initialize()` falha em silêncio (`.catch(() => {})`).
 * Mantém a invariante "dev local nunca depende da site key real".
 */
const DEV_PLACEHOLDER_SITE_KEY = 'app-check-debug-mode-nao-usa-site-key';

function bootstrapAppCheck(): AppCheck | null {
  if (isDev) {
    // T-A3 — dev/emulador por Debug Token, jamais pela site key real.
    if (!appCheckDebugToken) {
      console.warn(
        '[App Check] REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN ausente: App Check não inicializado ' +
          'neste ambiente de desenvolvimento. Chamadas contra os emuladores seguem funcionando; ' +
          'para chamar funções reais, registre um debug token no Console (App Check → Gerenciar ' +
          'tokens de depuração) e defina a variável em .env.local.'
      );
      return null;
    }
    // O SDK lê a flag via `getGlobal()` (@firebase/util), que no navegador resolve para `self` —
    // o mesmo objeto que `globalThis`. Precisa estar setada ANTES de `initializeAppCheck`.
    (globalThis as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN =
      appCheckDebugToken;
    return initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(DEV_PLACEHOLDER_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  }

  if (!appCheckSiteKey) {
    // T-A2 — diagnóstico explícito. CRA inlina a variável em build time: ausente aqui significa
    // bundle publicado sem App Check, cujo sintoma final seria um 401 genérico indistinguível de
    // problema de claim/ADMIN_UID (modo de falha da REEDY-16). Falha visível em vez de silenciosa.
    if (isBundleBuild) {
      const message =
        '[App Check] REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_SITE_KEY ausente neste build. ' +
        'O reedy-admin não consegue emitir token de App Check e as Cloud Functions administrativas ' +
        'responderão 401 (unauthenticated). Configure a site key do reCAPTCHA v3 do projeto Firebase ' +
        'correspondente antes de publicar.';
      console.error(message);
      throw new Error(message);
    }
    // Ambiente de teste automatizado sem site key: nada a inicializar.
    return null;
  }

  return initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

/**
 * Instância de App Check. `null` apenas fora de bundle publicado (dev sem debug token e testes):
 * o único consumidor direto é o download de CSV, que monta o header `X-Firebase-AppCheck` na mão.
 */
export const appCheck: AppCheck | null = bootstrapAppCheck();

// Emuladores no dev
if (isDev) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  connectFunctionsEmulator(functionsSa, '127.0.0.1', 5001);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
}

export default app;
